import React from 'react';
import { Plus, ChevronDown, ChevronUp, Lightbulb, MoveHorizontal, NotebookPen } from 'lucide-react';
import { format } from 'date-fns';
import RichTextEditor from '@/components/shared/RichTextEditor';
import { useDesign } from '@/contexts/DesignContext';

// Compact collapsible panel toggling between Suggested Tasks and a rich-text Daily Note.
// The Suggestions|Notes toggle stays visible even when collapsed; tapping a mode expands.
export default function SuggestionsNotesPanel({
  selectedDate,
  collapsed,
  setCollapsed,
  mode,
  setMode,
  getSuggestionsForDate,
  addSuggestion,
  handleMoveTask,
  dailyNotes,
  saveDailyNote,
  variant = 'day', // 'day' | 'week' (unused for labels now, kept for callers)
  testIdPrefix = '',
}) {
  const { isStudio } = useDesign();
  const dateKey = format(selectedDate, 'yyyy-MM-dd');
  const isNotes = mode === 'notes';
  const dayLabel = format(selectedDate, 'EEEE');

  const selectMode = (m) => {
    setMode(m);
    if (collapsed) setCollapsed(false);
  };

  const modeBtnClass = (active) =>
    `flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors ${
      active
        ? 'bg-white dark:bg-[#2d2f33] text-indigo-700 dark:text-indigo-200 shadow-sm'
        : 'text-indigo-500/80 dark:text-indigo-300/60 hover:text-indigo-700 dark:hover:text-indigo-200'
    }`;

  const suggestions = collapsed ? [] : getSuggestionsForDate(selectedDate);

  // Studio (106): the indigo panel becomes a plain card whose header is the
  // Suggestions / Notes segmented control. "Move" still opens MoveTaskDialog —
  // a weekday override (PLN-B43), not a task move.
  if (isStudio) {
    return (
      <section className="st-card" data-testid={`${testIdPrefix}suggestions-card`}>
        <div className="st-wh">
          <div className="st-segc" role="tablist" aria-label="Panel mode">
            <button
              type="button" role="tab" aria-selected={!isNotes}
              className={!isNotes ? 'st-on' : undefined}
              onClick={() => selectMode('suggestions')}
              data-testid={`${testIdPrefix}panel-mode-suggestions`}
            >
              <Lightbulb />Suggestions
            </button>
            <button
              type="button" role="tab" aria-selected={isNotes}
              className={isNotes ? 'st-on' : undefined}
              onClick={() => selectMode('notes')}
              data-testid={`${testIdPrefix}panel-mode-notes`}
            >
              <NotebookPen />Notes
            </button>
          </div>
          <div className="st-wr">
            <span>{dayLabel}</span>
            <button
              type="button"
              className="st-ibtn"
              style={{ width: 28, height: 28 }}
              onClick={() => setCollapsed(!collapsed)}
              aria-expanded={!collapsed}
              title={collapsed ? 'Expand' : 'Collapse'}
              data-testid={`${testIdPrefix}collapse-suggestions-btn`}
            >
              {collapsed ? <ChevronDown /> : <ChevronUp />}
            </button>
          </div>
        </div>
        {!collapsed && (
          <div className="st-wb">
            {isNotes ? (
              <RichTextEditor
                key={dateKey}
                content={dailyNotes[dateKey] || ''}
                onChange={(html) => saveDailyNote(dateKey, html)}
                placeholder={`Notes for ${dayLabel}… saves automatically`}
                minHeight="70px"
                minimal
              />
            ) : (
              <div className="st-sugs">
                {suggestions.map((suggestion, idx) => (
                  <div className="st-sug" key={idx}>
                    <Plus />
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => addSuggestion(suggestion)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addSuggestion(suggestion); } }}
                      data-testid={`${testIdPrefix}add-suggestion-${idx}`}
                    >
                      {suggestion}
                    </span>
                    <button
                      type="button"
                      className="st-mv"
                      onClick={() => handleMoveTask(suggestion)}
                      title="Move to a different weekday"
                      data-testid={`${testIdPrefix}move-suggestion-${idx}`}
                    >
                      Move
                    </button>
                  </div>
                ))}
                {suggestions.length === 0 && (
                  <p className="st-lbl" style={{ padding: '2px 0' }}>No suggested tasks for {dayLabel}</p>
                )}
              </div>
            )}
          </div>
        )}
      </section>
    );
  }

  return (
    <div
      className="rounded-xl border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/30 dark:bg-indigo-500/10 shadow-sm"
      data-testid={`${testIdPrefix}suggestions-card`}
    >
      {/* Header row — always visible */}
      <div className="flex items-center gap-2 px-2 py-1.5">
        <div className="flex items-center rounded-lg bg-indigo-100/80 dark:bg-indigo-500/15 p-0.5" role="tablist" aria-label="Panel mode">
          <button
            role="tab"
            aria-selected={!isNotes}
            onClick={() => selectMode('suggestions')}
            className={modeBtnClass(!isNotes)}
            data-testid={`${testIdPrefix}panel-mode-suggestions`}
          >
            <Lightbulb className="w-3.5 h-3.5" />
            Suggestions
          </button>
          <button
            role="tab"
            aria-selected={isNotes}
            onClick={() => selectMode('notes')}
            className={modeBtnClass(isNotes)}
            data-testid={`${testIdPrefix}panel-mode-notes`}
          >
            <NotebookPen className="w-3.5 h-3.5" />
            Notes
          </button>
        </div>
        <span className="flex-1 text-right text-[11px] text-slate-400 dark:text-slate-500 truncate pr-1">
          {format(selectedDate, 'EEE, MMM d')}
        </span>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded-lg bg-indigo-100 dark:bg-indigo-500/20 hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors flex-shrink-0"
          data-testid={`${testIdPrefix}collapse-suggestions-btn`}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed
            ? <ChevronDown className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
            : <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />}
        </button>
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="px-2 pb-2">
          {isNotes ? (
            <RichTextEditor
              key={dateKey}
              content={dailyNotes[dateKey] || ''}
              onChange={(html) => saveDailyNote(dateKey, html)}
              placeholder={`Notes for ${dayLabel}… saves automatically`}
              minHeight="70px"
              minimal
            />
          ) : (
            <div className="space-y-1">
              {suggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1.5 p-2 rounded-lg bg-card border border-slate-100 dark:border-white/10 hover:border-indigo-200 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/10 transition-colors"
                >
                  <button
                    onClick={() => addSuggestion(suggestion)}
                    className="flex-1 text-left flex items-center"
                    data-testid={`${testIdPrefix}add-suggestion-${idx}`}
                  >
                    <Plus className="w-4 h-4 mr-1.5 text-indigo-500 flex-shrink-0" />
                    <span className="line-clamp-2 text-xs text-slate-700 dark:text-slate-200">{suggestion}</span>
                  </button>
                  <button
                    onClick={() => handleMoveTask(suggestion)}
                    className="p-1.5 rounded bg-slate-100 dark:bg-[#3a3d41] hover:bg-slate-200 transition-colors flex-shrink-0"
                    title="Move to different day"
                    data-testid={`${testIdPrefix}move-suggestion-${idx}`}
                  >
                    <MoveHorizontal className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                  </button>
                </div>
              ))}
              {suggestions.length === 0 && (
                <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-2">
                  No suggested tasks for {dayLabel}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
